/**
 * Smith Protective — Territory Map (Iter245 rewrite).
 *
 * Ported from `/leads-map` and adapted for the Smith Protective pipeline:
 *   • Standard Smith AppHeader + Sidebar for visual consistency.
 *   • Google Maps JS API with clustered pins colored by pipeline stage.
 *   • Retractable "Filters & Legend" panel (segment + funnel + hide-lost
 *     + radius filter). Legend collapses independently inside the panel.
 *   • Visible-count badge at top-left updating live as filters change.
 *   • Route builder: check prospects from the preview card, floating
 *     "Traçar Rota" button appears with 2+ selected, opens Google Maps
 *     with sequential stops and shows estimated total distance beforehand.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { APIProvider, Map as GMap, useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import {
  Loader2, Map as MapIcon, ArrowLeft, SlidersHorizontal, X, Navigation,
  Route as RouteIcon, MapPin, Search, Trash2, ChevronDown, ChevronUp,
  Crosshair, Shield, Building2, Check,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
                  || process.env.REACT_APP_GOOGLE_MAPS_KEY;

// Palette shared with the CRM Kanban so pin colour == column colour.
const STAGE_META = {
  "New Prospect":    { label: "Novo",              color: "#3B82F6" },
  "Researching":     { label: "Pesquisando",       color: "#0EA5E9" },
  "Qualified":       { label: "Qualificado",       color: "#A855F7" },
  "Contacted":       { label: "Contatado",         color: "#8B5CF6" },
  "Meeting Booked":  { label: "Reunião agendada",  color: "#F59E0B" },
  "Proposal Sent":   { label: "Proposta enviada",  color: "#F97316" },
  "Negotiation":     { label: "Negociação",        color: "#EAB308" },
  "Won":             { label: "Fechado",           color: "#10B981" },
  "Lost":            { label: "Perdido",           color: "#9CA3AF" },
  "On Hold":         { label: "Em espera",         color: "#64748B" },
};
const stageMeta = (s) => STAGE_META[s]
  || { label: s || "—", color: "#6B7280" };

const RADII_KM = [0, 5, 10, 25, 50];

const distKm = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180)
      * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

// Sequential distance across the ordered route (last stop = final leg).
const routeTotalKm = (stops) => {
  if (stops.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < stops.length; i++) total += distKm(stops[i - 1], stops[i]);
  return total;
};


function MapContent({ prospects, colorFor, onSelect,
                       routeIds, routePath, focusCenter }) {
  const map = useMap();
  const clusterRef = useRef(null);
  const polyRef = useRef(null);
  const circleRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const g = window.google.maps;
    const markers = prospects.map((p) => {
      const inRoute = routeIds.has(p.id);
      const m = new g.Marker({
        position: { lat: p.lat, lng: p.lng },
        title: (p.company || {}).name || p.company_name || "Prospect",
        icon: {
          path: g.SymbolPath.CIRCLE,
          scale: inRoute ? 11 : 8,
          fillColor: colorFor(p),
          fillOpacity: 0.92,
          strokeColor: inRoute ? "#DC2626" : "#0B1120",
          strokeWeight: inRoute ? 3 : 2,
        },
      });
      m.addListener("click", () => onSelect(p));
      return m;
    });
    clusterRef.current = new MarkerClusterer({ map, markers });
    return () => { clusterRef.current?.clearMarkers(); };
  }, [map, prospects, routeIds, colorFor, onSelect]);

  // Fit bounds once when data arrives.
  const fitted = useRef(false);
  useEffect(() => {
    if (!map || !window.google?.maps || fitted.current || prospects.length === 0) return;
    const b = new window.google.maps.LatLngBounds();
    prospects.forEach((p) => b.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(b, 60);
    fitted.current = true;
  }, [map, prospects]);

  // Route polyline (order matters — matches the sequential dir=&waypoints=).
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    polyRef.current?.setMap(null);
    if (routePath.length >= 2) {
      polyRef.current = new window.google.maps.Polyline({
        map, path: routePath, strokeColor: "#DC2626",
        strokeOpacity: 0.85, strokeWeight: 4, geodesic: true,
      });
    }
    return () => polyRef.current?.setMap(null);
  }, [map, routePath]);

  // Geo-filter radius circle.
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    circleRef.current?.setMap(null);
    if (focusCenter?.radiusKm > 0) {
      circleRef.current = new window.google.maps.Circle({
        map, center: { lat: focusCenter.lat, lng: focusCenter.lng },
        radius: focusCenter.radiusKm * 1000,
        strokeColor: "#DC2626", strokeWeight: 2,
        fillColor: "#DC2626", fillOpacity: 0.06,
      });
      map.panTo({ lat: focusCenter.lat, lng: focusCenter.lng });
    }
    return () => circleRef.current?.setMap(null);
  }, [map, focusCenter]);

  return null;
}


export default function SmithMap() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapError, setMapError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [route, setRoute] = useState([]);

  // Filters
  const [segmentFilter, setSegmentFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [hideLost, setHideLost] = useState(true);   // BD-friendly default
  const [geoQuery, setGeoQuery] = useState("");
  const [geoCenter, setGeoCenter] = useState(null);
  const [radiusKm, setRadiusKm] = useState(10);
  const [geoBusy, setGeoBusy] = useState(false);

  // Panel state (persisted per-user)
  const [panelOpen, setPanelOpen] = useState(() => {
    try { return localStorage.getItem("smith.map.panelOpen") !== "0"; }
    catch { return true; }
  });
  const [legendOpen, setLegendOpen] = useState(() => {
    try { return localStorage.getItem("smith.map.legendOpen") !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("smith.map.panelOpen", panelOpen ? "1" : "0"); }
    catch { /* private mode */ }
  }, [panelOpen]);
  useEffect(() => {
    try { localStorage.setItem("smith.map.legendOpen", legendOpen ? "1" : "0"); }
    catch { /* private mode */ }
  }, [legendOpen]);

  // Fetch prospects.
  useEffect(() => {
    window.gm_authFailure = () => setMapError(true);
    axios.get(`${API}/smith/prospects/map`, { params: { limit: 3000 } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error("Falha ao carregar o mapa"))
      .finally(() => setLoading(false));
    return () => { window.gm_authFailure = undefined; };
  }, []);

  // ── Derived collections ──────────────────────────────────────────
  const segments = useMemo(() => {
    const bag = new Map();
    items.forEach((p) => {
      const s = p.segment || "—";
      bag.set(s, (bag.get(s) || 0) + 1);
    });
    return [...bag.entries()].sort((a, b) => b[1] - a[1]);
  }, [items]);

  const stages = useMemo(() => {
    const seen = new Set(items.map((p) => p.pipeline_status).filter(Boolean));
    // Keep display order stable (rank by canonical STAGE_META order).
    return Object.keys(STAGE_META).filter((k) => seen.has(k));
  }, [items]);

  const colorFor = useCallback((p) => stageMeta(p.pipeline_status).color, []);

  const visible = useMemo(() => items.filter((p) => {
    if (segmentFilter && p.segment !== segmentFilter) return false;
    if (stageFilter && p.pipeline_status !== stageFilter) return false;
    if (hideLost && p.pipeline_status === "Lost") return false;
    if (geoCenter && geoCenter.radiusKm > 0 && distKm(geoCenter, p) > geoCenter.radiusKm) return false;
    return true;
  }), [items, segmentFilter, stageFilter, hideLost, geoCenter]);

  // ── Route builder ────────────────────────────────────────────────
  const routeIds = useMemo(() => new Set(route.map((r) => r.id)), [route]);
  const routePath = useMemo(() => route.map((r) => ({ lat: r.lat, lng: r.lng })), [route]);
  const totalRouteKm = useMemo(() => routeTotalKm(route), [route]);

  const toggleRoute = (p) => {
    setRoute((r) => routeIds.has(p.id)
      ? r.filter((x) => x.id !== p.id)
      : [...r, p]);
    if (!routeIds.has(p.id)) {
      const name = (p.company || {}).name || p.company_name || "prospect";
      toast.success(`📍 ${name} adicionado à rota`, { duration: 1600 });
    }
  };

  const clearRoute = () => { setRoute([]); toast.info("Rota limpa"); };

  const navUrl = useMemo(() => {
    if (route.length === 0) return "";
    const pts = route.map((r) => `${r.lat},${r.lng}`);
    // Google Maps directions API: first stop = origin, last = destination,
    // in-between = waypoints (max 9 for the classic dir URL).
    const origin = pts[0];
    const dest = pts[pts.length - 1];
    const way = pts.slice(1, -1).slice(0, 9).join("|");
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}${way ? `&waypoints=${encodeURIComponent(way)}` : ""}&travelmode=driving`;
  }, [route]);

  const openRoute = () => {
    if (route.length < 2) return;
    const km = totalRouteKm.toFixed(1);
    toast.success(`Rota com ${route.length} paradas · ~${km} km`,
                     { duration: 3000 });
    setTimeout(() => window.open(navUrl, "_blank", "noopener,noreferrer"), 350);
  };

  // ── Geo search ───────────────────────────────────────────────────
  const geoSearch = async (e) => {
    e?.preventDefault?.();
    if (geoQuery.trim().length < 3) return;
    setGeoBusy(true);
    try {
      const { data } = await axios.get(`${API}/geocode/search`,
                                             { params: { q: geoQuery } });
      const r = (data.results || [])[0];
      if (r) {
        setGeoCenter({ ...r, radiusKm: Math.max(radiusKm, 5) });
        if (radiusKm === 0) setRadiusKm(10);
        toast.success(`📍 ${r.label}`);
      } else toast.info("Endereço não encontrado");
    } catch (_e) { toast.error("Falha no geocode"); }
    finally { setGeoBusy(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation indisponível");
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeoCenter({
        lat: pos.coords.latitude, lng: pos.coords.longitude,
        label: "Minha localização", radiusKm: Math.max(radiusKm, 5),
      }),
      () => toast.error("Permissão de localização negada"));
  };

  useEffect(() => {
    if (geoCenter) setGeoCenter((c) => c && { ...c, radiusKm });
  }, [radiusKm]); // eslint-disable-line

  return (
    <div className="min-h-screen text-white"
         style={{ background: "linear-gradient(180deg,#0B1120 0%,#000 100%)" }}
         data-testid="smith-map-page">
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-map-header"
          title={<span className="inline-flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#DC2626]" />
                    <span>Smith · Territory Map</span>
                    <span data-testid="smith-map-visible-count"
                          className="text-[11px] font-black px-2 py-0.5 rounded-full text-white tabular-nums"
                          style={{ background: "#DC2626" }}>
                      {visible.length} visible
                    </span>
                  </span>}
          subtitle={`${items.length.toLocaleString("en-US")} accounts georreferenciados`}
        />

        <main className="p-4 sm:p-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <Link to="/smith/prospects"
                    data-testid="smith-map-back"
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-400 hover:text-[#DC2626] transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Voltar para prospects
            </Link>
            {route.length > 0 && (
              <span data-testid="smith-map-route-count"
                      className="text-[11px] font-bold text-slate-300">
                {route.length} {route.length === 1 ? "parada" : "paradas"}{" "}
                na rota
                {route.length >= 2 && (
                  <> · <span className="text-[#DC2626] tabular-nums">
                    ~{totalRouteKm.toFixed(1)} km
                  </span></>
                )}
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <div className="relative rounded-xl border border-white/10 overflow-hidden"
                    style={{ minHeight: 640, isolation: "isolate",
                              height: "calc(100vh - 220px)" }}
                    data-testid="smith-map-canvas">
              {(!GMAPS_KEY || mapError) ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center p-6">
                  <MapIcon className="w-8 h-8 text-slate-500" />
                  <p className="text-[13px] font-semibold">Google Maps indisponível</p>
                  <p className="text-[12px] text-slate-400 max-w-sm">
                    Verifique se a chave <code className="text-amber-300">REACT_APP_GOOGLE_MAPS_API_KEY</code>{" "}
                    tem "Maps JavaScript API" habilitada.
                  </p>
                </div>
              ) : (
                <div className="absolute inset-0">
                  <APIProvider apiKey={GMAPS_KEY}>
                    <GMap style={{ width: "100%", height: "100%" }}
                        defaultCenter={{ lat: 28.5384, lng: -81.3789 }}
                        defaultZoom={7}
                        gestureHandling="greedy"
                        streetViewControl={false}
                        mapTypeControl={false}
                        fullscreenControl={false}>
                      <MapContent prospects={visible} colorFor={colorFor}
                                    onSelect={setSelected}
                                    routeIds={routeIds}
                                    routePath={routePath}
                                    focusCenter={geoCenter} />
                    </GMap>
                  </APIProvider>
                </div>
              )}

              {/* ── Retractable Filters & Legend panel (top-right) ── */}
              <div className="absolute top-3 right-3 z-10 w-[300px] max-w-[calc(100%-24px)]"
                      data-testid="smith-map-panel">
                <button type="button"
                          onClick={() => setPanelOpen((o) => !o)}
                          data-testid="smith-map-panel-toggle"
                          className="w-full h-11 px-3.5 rounded-xl bg-[#0F172A] border border-white/10 shadow-lg flex items-center gap-2 text-[12.5px] font-bold text-white">
                  <SlidersHorizontal className="w-4 h-4 text-[#DC2626]" />
                  Filters & Legend
                  <span className="ml-auto">
                    {panelOpen ? <ChevronUp className="w-4 h-4" />
                                 : <ChevronDown className="w-4 h-4" />}
                  </span>
                </button>
                {panelOpen && (
                  <div className="mt-2 rounded-xl bg-[#0F172A] border border-white/10 shadow-xl p-3.5 space-y-3 max-h-[64vh] overflow-y-auto"
                          data-testid="smith-map-panel-body">
                    {/* Segment filter */}
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">
                        Segmento
                      </div>
                      <select value={segmentFilter}
                                onChange={(e) => setSegmentFilter(e.target.value)}
                                data-testid="smith-map-segment-filter"
                                className="w-full h-9 rounded-lg bg-black/40 border border-white/10 px-2.5 text-[12px] text-white">
                        <option value="">All sectors ({items.length})</option>
                        {segments.map(([s, n]) => (
                          <option key={s} value={s}>{s} ({n})</option>
                        ))}
                      </select>
                    </div>

                    {/* Stage filter */}
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">
                        Etapa do funil
                      </div>
                      <select value={stageFilter}
                                onChange={(e) => setStageFilter(e.target.value)}
                                data-testid="smith-map-stage-filter"
                                className="w-full h-9 rounded-lg bg-black/40 border border-white/10 px-2.5 text-[12px] text-white">
                        <option value="">All funnel stages</option>
                        {stages.map((st) => (
                          <option key={st} value={st}>{stageMeta(st).label}</option>
                        ))}
                      </select>
                      <label className="flex items-center gap-2 text-[12px] cursor-pointer mt-2"
                                data-testid="smith-map-hide-lost">
                        <input type="checkbox" checked={hideLost}
                                 onChange={(e) => setHideLost(e.target.checked)}
                                 className="accent-[#DC2626] w-4 h-4" />
                        Hide lost leads
                      </label>
                    </div>

                    {/* Radius filter */}
                    <div className="pt-2 border-t border-white/10 space-y-2">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
                        Filtro por raio
                      </div>
                      <form onSubmit={geoSearch} className="flex gap-1.5">
                        <input value={geoQuery}
                                 onChange={(e) => setGeoQuery(e.target.value)}
                                 placeholder="Endereço ou cidade…"
                                 data-testid="smith-map-geo-input"
                                 className="flex-1 h-9 rounded-lg bg-black/40 border border-white/10 px-2.5 text-[12px] text-white placeholder-slate-500" />
                        <button type="submit" disabled={geoBusy}
                                  data-testid="smith-map-geo-search"
                                  className="h-9 w-9 shrink-0 rounded-lg bg-[#DC2626] text-white flex items-center justify-center disabled:opacity-50">
                          {geoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Search className="w-3.5 h-3.5" />}
                        </button>
                        <button type="button" onClick={useMyLocation}
                                  data-testid="smith-map-geo-mylocation"
                                  title="Usar minha localização"
                                  className="h-9 w-9 shrink-0 rounded-lg border border-white/10 flex items-center justify-center hover:border-[#DC2626]">
                          <Crosshair className="w-3.5 h-3.5" />
                        </button>
                      </form>
                      <div className="flex gap-1.5">
                        {RADII_KM.map((r) => (
                          <button key={r} type="button"
                                    onClick={() => setRadiusKm(r)}
                                    data-testid={`smith-map-radius-${r}`}
                                    className={`flex-1 h-8 rounded-lg border text-[11px] font-bold ${
                                      radiusKm === r
                                        ? "bg-[#DC2626] text-white border-[#DC2626]"
                                        : "border-white/10 text-slate-300"}`}>
                            {r === 0 ? "Off" : `${r}km`}
                          </button>
                        ))}
                      </div>
                      {geoCenter && (
                        <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                          <MapPin className="w-3 h-3 text-[#DC2626]" />
                          <span className="truncate flex-1">{geoCenter.label}</span>
                          <button type="button" onClick={() => setGeoCenter(null)}
                                    data-testid="smith-map-geo-clear">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Legend (collapsible on its own) */}
                    <div className="pt-2 border-t border-white/10">
                      <button type="button"
                                onClick={() => setLegendOpen((o) => !o)}
                                data-testid="smith-map-legend-toggle"
                                className="w-full flex items-center justify-between text-[10px] uppercase tracking-widest font-bold text-slate-400 hover:text-white">
                        Legenda
                        {legendOpen ? <ChevronUp className="w-3.5 h-3.5" />
                                       : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                      {legendOpen && (
                        <div className="mt-2 flex flex-wrap gap-1.5"
                                data-testid="smith-map-legend">
                          {stages.map((st) => (
                            <span key={st}
                                    data-testid={`smith-map-legend-${st}`}
                                    className="text-[10px] px-2 py-1 rounded-full border border-white/10 bg-black/30 inline-flex items-center gap-1 text-slate-300">
                              <span className="w-2 h-2 rounded-full"
                                      style={{ background: stageMeta(st).color }} />
                              {stageMeta(st).label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Prospect preview card (bottom-left) ── */}
              {selected && <PreviewCard prospect={selected}
                                              isInRoute={routeIds.has(selected.id)}
                                              onToggleRoute={() => toggleRoute(selected)}
                                              onClose={() => setSelected(null)} />}

              {/* ── Floating "Traçar Rota" button (bottom-center) ── */}
              {route.length >= 2 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20"
                        data-testid="smith-map-route-cta">
                  <div className="rounded-2xl bg-[#0F172A] border-2 border-[#DC2626] shadow-2xl px-4 py-2 flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <RouteIcon className="w-5 h-5 text-[#DC2626]" />
                      <div className="text-left">
                        <div className="text-[12px] font-extrabold text-white leading-tight"
                                data-testid="smith-map-route-summary">
                          {route.length} paradas ·{" "}
                          <span className="text-[#DC2626] tabular-nums">
                            ~{totalRouteKm.toFixed(1)} km
                          </span>
                        </div>
                        <div className="text-[10.5px] text-slate-400 leading-tight">
                          Abre em nova aba no Google Maps
                        </div>
                      </div>
                    </div>
                    <button type="button" onClick={clearRoute}
                              data-testid="smith-map-route-clear"
                              title="Limpar rota"
                              className="h-9 w-9 rounded-lg border border-white/10 flex items-center justify-center hover:border-[#DC2626]">
                      <Trash2 className="w-4 h-4 text-slate-400" />
                    </button>
                    <button type="button" onClick={openRoute}
                              data-testid="smith-map-route-open"
                              className="h-10 px-4 rounded-lg bg-[#DC2626] hover:bg-[#B91C1C] text-white text-[12.5px] font-extrabold uppercase tracking-widest inline-flex items-center gap-1.5">
                      <Navigation className="w-3.5 h-3.5" />
                      Traçar Rota
                    </button>
                  </div>
                </div>
              )}

              {/* ── Route stops list (top-left, above map) ── */}
              {route.length > 0 && (
                <div className="absolute top-3 left-3 z-10 w-[240px] max-w-[calc(100%-24px)] rounded-xl bg-[#0F172A] border border-white/10 shadow-lg overflow-hidden"
                        data-testid="smith-map-route-panel">
                  <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
                    <RouteIcon className="w-4 h-4 text-[#DC2626]" />
                    <span className="text-[11px] font-extrabold uppercase tracking-widest text-white flex-1">
                      Rota ({route.length})
                    </span>
                    <button type="button" onClick={clearRoute}
                              data-testid="smith-map-route-clear-panel"
                              className="text-slate-400 hover:text-white">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <ul className="max-h-[220px] overflow-y-auto divide-y divide-white/5">
                    {route.map((r, i) => {
                      const name = (r.company || {}).name || r.company_name || "—";
                      return (
                        <li key={r.id}
                                data-testid={`smith-map-route-stop-${i}`}
                                className="px-3 py-1.5 flex items-center gap-2 text-[11.5px] text-slate-200">
                          <span className="w-5 h-5 rounded-full bg-[#DC2626]/15 text-[#DC2626] text-[10px] font-extrabold flex items-center justify-center shrink-0">
                            {i + 1}
                          </span>
                          <span className="truncate flex-1">{name}</span>
                          <button type="button" onClick={() => toggleRoute(r)}
                                    title="Remover"
                                    className="text-slate-500 hover:text-rose-400 shrink-0">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}


/** Small preview card shown when a pin is clicked. Contains the
 *  route-checkbox that adds/removes the prospect from the current route
 *  (the "checkbox in each pin" from the spec). */
function PreviewCard({ prospect, isInRoute, onToggleRoute, onClose }) {
  const name = (prospect.company || {}).name || prospect.company_name || "Prospect";
  const meta = stageMeta(prospect.pipeline_status);
  const acv = prospect.opportunity?.acv_est;
  return (
    <div className="absolute bottom-3 left-3 z-10 w-[320px] max-w-[calc(100%-24px)] rounded-xl bg-[#0F172A] border border-white/10 shadow-2xl p-4"
         data-testid="smith-map-preview">
      <div className="flex items-start gap-2 mb-2">
        <Building2 className="w-4 h-4 text-[#DC2626] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-extrabold text-white leading-snug break-words">
            {name}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            {prospect.segment || "—"} · {prospect.city || ""}{prospect.state ? `, ${prospect.state}` : ""}
          </div>
        </div>
        <button type="button" onClick={onClose}
                  data-testid="smith-map-preview-close"
                  className="text-slate-500 hover:text-white shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                style={{ background: `${meta.color}22`, color: meta.color }}
                data-testid="smith-map-preview-stage">
          {meta.label}
        </span>
        {acv > 0 && (
          <span className="text-[10px] font-bold text-emerald-300 tabular-nums"
                  data-testid="smith-map-preview-acv">
            ACV ${Math.round(acv).toLocaleString("en-US")}
          </span>
        )}
      </div>
      <label className={`flex items-center gap-2 h-10 px-3 rounded-lg cursor-pointer border font-bold text-[12px] transition-colors ${
        isInRoute
          ? "bg-[#DC2626]/15 border-[#DC2626] text-[#DC2626]"
          : "border-white/10 text-slate-200 hover:border-[#DC2626]"}`}
             data-testid="smith-map-preview-route-toggle">
        <input type="checkbox" checked={isInRoute} onChange={onToggleRoute}
                 className="accent-[#DC2626] w-4 h-4" />
        {isInRoute
          ? <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Na rota</span>
          : "Adicionar à rota"}
      </label>
      <Link to={`/smith/prospects/${prospect.id}`}
              data-testid="smith-map-preview-open"
              className="mt-2 block text-center h-9 rounded-lg bg-white/5 hover:bg-white/10 text-[11.5px] font-bold text-slate-200 leading-9">
        Abrir detalhes →
      </Link>
    </div>
  );
}
